import React, { useState } from 'react';
import {
  Box,
  Stack,
  Paper,
  Typography,
  Button,
  Chip,
  Collapse,
} from '@mui/material';
import { CheckCircle2, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { getDownloadLink } from '../../../../../../../../utils/api_service/assessment_api';
import { convertSecondsToTimeTaken } from '../../../../../../../../utils/helpers/time';
import { AssessmentQuestionResult } from '../../../../../../../../utils/types/assessment_types';
import { SkillQuestionAndAnswer } from './SkillQuestionAndAnswer';

type QuestionCollapsibleProps = {
  assessmentQuestionResult: AssessmentQuestionResult;
  updateDetailedQuestionArray: (
    questionArray: AssessmentQuestionResult,
    getLinkStatus?: boolean
  ) => void;
  showSystemAnswer?: boolean;
  showVideo?: boolean;
};

export const QuestionCollapsible: React.FC<QuestionCollapsibleProps> = ({
  assessmentQuestionResult,
  updateDetailedQuestionArray,
  showSystemAnswer = false,
  showVideo = false,
}) => {
  const [resultOpen, setResultOpen] = useState<boolean>(false);

  const getVideoLink = () => {
    if (!assessmentQuestionResult.videoUrl) {
      getDownloadLink(assessmentQuestionResult.id).then((response) => {
        assessmentQuestionResult.videoUrl = response.url;
        updateDetailedQuestionArray(assessmentQuestionResult, true);
      });
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: resultOpen ? 'action.hover' : 'background.paper',
        transition: 'all 0.2s ease',
      }}
    >
      <Box sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              {assessmentQuestionResult.isCorrect ? (
                <CheckCircle2 color="#2e7d32" size={20} />
              ) : (
                <XCircle color="#d32f2f" size={20} />
              )}
            </Box>
            <Typography variant="body2" fontWeight={600} color="text.primary">
              {assessmentQuestionResult.systemQuestion}
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0, ml: 1 }}>
              <Chip
                size="small"
                label={`${assessmentQuestionResult.score || 0} pts`}
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={
                  assessmentQuestionResult.timeExpired
                    ? 'Time Expired'
                    : convertSecondsToTimeTaken(
                        assessmentQuestionResult.timeTaken,
                      ) || '0:00'
                }
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }}
              />
            </Stack>
          </Stack>

          {assessmentQuestionResult.userAnswer !== undefined && (
            <Button
              size="small"
              variant={resultOpen ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => {
                setResultOpen(!resultOpen);
                getVideoLink();
              }}
              endIcon={resultOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              sx={{ textTransform: 'none', fontWeight: 600, flexShrink: 0 }}
            >
              {resultOpen ? 'Hide Answer' : 'Show Answer'}
            </Button>
          )}
        </Stack>
      </Box>

      <Collapse in={resultOpen && assessmentQuestionResult.userAnswer !== undefined}>
        <Box sx={{ p: 2.5, bgcolor: 'background.paper', borderTop: '1px solid', borderColor: 'divider' }}>
          <SkillQuestionAndAnswer
            key={assessmentQuestionResult.id}
            assessmentQuestionResult={assessmentQuestionResult}
            updateDetailedQuestionArray={updateDetailedQuestionArray}
            setResultOpen={setResultOpen}
            showSystemAnswer={showSystemAnswer}
            showVideo={showVideo}
          />
        </Box>
      </Collapse>
    </Paper>
  );
};
